/**
 * One Group's site look (Monica Baker, 13 Oct 2026: "please can we go with the skyline, navy + gold ribbon and a
 * watermark O"). Colours from her letterhead file (One_Finance_Email_Letterhead_Designs.html): navy #071e3e,
 * navy-2 #102c54, gold #c5a06a. Used by the site header (NavbarWithCallToAction) and footer (FooterWithFourColumns).
 * The email header/footer twin lives on the tenant record (branding.bannerImage + emailFooterText).
 */
import React from "react";
import { Box, BoxProps, HStack, Text } from "@chakra-ui/react";

export const ONE_NAVY = "#071e3e";
export const ONE_NAVY_2 = "#102c54";
export const ONE_GOLD = "#c5a06a";

/** The "O / ONE" mark drawn in type (the logo PNG has a white background, so it can't sit on navy). */
export const OneMark: React.FC<{ siteName?: string; tagline?: string }> = ({ siteName = "One Group Australia", tagline }) => (
  <HStack spacing={{ base: 2, md: 3 }} align="center" color="white">
    <Box textAlign="center" lineHeight={1} w={{ base: "38px", md: "48px" }} flexShrink={0}>
      <Text as="span" display="block" fontFamily="Georgia, 'Times New Roman', serif" fontSize={{ base: "38px", md: "48px" }} lineHeight={0.85}>O</Text>
      <Text as="span" display="block" fontFamily="Arial, sans-serif" fontWeight={700} fontSize={{ base: "9px", md: "10px" }} letterSpacing="3px" pl="3px" mt="5px">ONE</Text>
    </Box>
    <Box w="1px" alignSelf="stretch" bg="whiteAlpha.500" display={{ base: "none", sm: "block" }} />
    <Box display={{ base: "none", sm: "block" }} minW={0}>
      <Text fontSize={{ sm: "md", md: "lg" }} letterSpacing="1.5px" textTransform="uppercase" whiteSpace="nowrap" fontWeight={400}>
        {siteName.replace(/ Australia$/i, "")} <Text as="b" fontWeight={800}>Australia</Text>
      </Text>
      {tagline && <Text fontSize="xs" letterSpacing="3px" color={ONE_GOLD} textTransform="uppercase" mt={1}>{tagline}</Text>}
    </Box>
  </HStack>
);

/** A large, faint serif "O" behind the content. Decorative only. */
export const WatermarkO: React.FC<BoxProps> = (props) => (
  <Box aria-hidden pointerEvents="none" position="absolute" fontFamily="Georgia, 'Times New Roman', serif" lineHeight={0.8}
    color="transparent" sx={{ WebkitTextStroke: `1.5px ${ONE_GOLD}` }} opacity={0.22} userSelect="none" {...props}>O</Box>
);

/**
 * Each business keeps the same navy look; only the RIBBON takes its accent (Monica, 13 Oct call: "use the accent
 * colours in the ribbon … green is property, gold for finance", solar bright yellow, water blue). The group = gold.
 */
export const RIBBON_ACCENTS: Record<string, string> = {
  "/property": "#0D7F6E",
  "/my-build": "#0D7F6E", // a client's build is One Property work
  "/finance": ONE_GOLD,
  "/solar": "#F2C230",
  "/water": "#6FAFD6",
};
export const ribbonAccentFor = (pathname: string) =>
  Object.entries(RIBBON_ACCENTS).find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? ONE_GOLD;

/** The navy + accent ribbon: a thin accent line (gold by default) with a fine navy-2 band beneath. */
export const GoldRibbon: React.FC<BoxProps & { accent?: string }> = ({ accent = ONE_GOLD, ...props }) => (
  <Box aria-hidden h="4px" bg={accent} bgGradient={`linear(90deg, ${accent} 0%, ${accent}cc 50%, ${accent} 100%)`} boxShadow={`0 1px 0 ${ONE_NAVY_2}`} {...props} />
);

/**
 * Brisbane's skyline in silhouette — the CBD towers (Infinity with its crown, 1 William St, Riparian, Aurora, 300 George)
 * shoulder to shoulder, then the river and the Story Bridge's cantilevers. 1200 × 140 viewBox.
 * `fit="slice"` fills a box (the header); `fit="meet"` keeps the whole skyline at its natural height (the footer).
 */
export const BrisbaneSkyline: React.FC<BoxProps & { fill?: string; fit?: "slice" | "meet" }> = ({ fill = "#ffffff", fit = "slice", ...props }) => (
  <Box aria-hidden pointerEvents="none" {...props}>
    <svg viewBox="0 0 1200 140" preserveAspectRatio={`xMidYMax ${fit}`} width="100%" height="100%" role="presentation" style={{ display: "block" }}>
      <g fill={fill}>
        {/* low-rise to the west */}
        <path d="M0 140v-26h22v-6h18v10h16v-16h20v8h18v-14h24v12h14v32z" />
        {/* the CBD — towers touching, varied tops */}
        <path d="M130 140V96h14V84h20v-8h18v64z" />
        <path d="M180 140V70h26v-6h10v76z" />
        <path d="M214 140V58l16-10 16 10v82z" />
        <path d="M244 140V76h24v64z" />
        {/* 300 George / Queen's Wharf — stepped */}
        <path d="M266 140V52h10V40h20v12h10v88z" />
        {/* Infinity — the tallest, stepped crown + mast */}
        <path d="M304 140V30h6V20h8V10h4V0h2v10h4v10h8v10h6v110z" />
        <path d="M340 140V64h22v-8h14v84z" />
        {/* 1 William St — slim, raked top */}
        <path d="M374 140V40l30-14v114z" />
        <path d="M402 140V72h26v68z" />
        {/* Riparian Plaza — twin ridges */}
        <path d="M426 140V46l9-12 9 12 9-12 9 12v94z" />
        <path d="M460 140V80h28V68h12v72z" />
        {/* Aurora — rounded crown */}
        <path d="M498 140V46a16 16 0 0 1 32 0v94z" />
        <path d="M528 140V74h20v-10h18v76z" />
        <path d="M564 140V86h30v-8h16v62z" />
        <path d="M608 140V100h40v40z" />
        {/* the river, then the Story Bridge */}
        <rect x="640" y="122" width="560" height="6" />
        <path d="M720 122l66-52 66 52h-12l-54-42-54 42z" />
        <path d="M852 122l66-52 66 52h-12l-54-42-54 42z" />
        <rect x="781" y="62" width="10" height="66" />
        <rect x="913" y="62" width="10" height="66" />
        <path d="M984 122l44-28 44 28h-10l-34-21-34 21z" />
        {/* Kangaroo Point to the east */}
        <path d="M1072 140v-24h30v-12h28v10h34v-8h36v34z" />
        <rect x="640" y="128" width="560" height="12" />
      </g>
    </svg>
  </Box>
);

/**
 * Monica's headline photo (13 Oct 2026, "Golden Hour Bridge Over the City": the Story Bridge and the CBD at sunset),
 * laid behind the header/footer with a navy veil so white text stays readable. `veil` sets how much navy covers it.
 */
export const BRIDGE_PHOTO = "/assets/brand/brisbane-story-bridge-golden-hour.jpg";
export const BRIDGE_PHOTO_SMALL = "/assets/brand/brisbane-story-bridge-golden-hour-900.jpg";
export const BridgePhoto: React.FC<BoxProps & { veil?: "header" | "footer" }> = ({ veil = "header", ...props }) => (
  <Box aria-hidden pointerEvents="none" position="absolute" inset={0} {...props}>
    <Box position="absolute" inset={0} bgImage={{ base: `url(${BRIDGE_PHOTO_SMALL})`, md: `url(${BRIDGE_PHOTO})` }}
      bgSize="cover" bgPosition={veil === "header" ? "center 38%" : "center 45%"} />
    <Box position="absolute" inset={0}
      bgGradient={veil === "header"
        ? `linear(90deg, ${ONE_NAVY} 0%, ${ONE_NAVY}f0 30%, ${ONE_NAVY}b3 60%, ${ONE_NAVY}80 100%)`
        : `linear(180deg, ${ONE_NAVY}33 0%, ${ONE_NAVY}99 60%, ${ONE_NAVY} 100%)`} />
  </Box>
);
